/**
 * The countries an organization may write its numbers like.
 *
 * Shared so the form control (`RegionSelect.vue`) and the formatter offer and
 * accept the same set — the reason `currencies.js` gives about currencies, for
 * the same reason.
 *
 * This is **not** a country picker: the postal address has its own, longer
 * list. It is the set of number conventions the app stands behind, and it
 * mirrors `App\Support\Regions::SUPPORTED` — a region offered here and refused
 * by the validator would be a setting that cannot be saved.
 *
 * The example is what makes the choice readable: nobody knows what « BE »
 * does to a number, and everybody recognises `1 234,50 €` on sight.
 */
const regions = [
    { code: 'CA', name: 'Canada' },
    { code: 'US', name: 'United States' },
    { code: 'FR', name: 'France' },
    { code: 'BE', name: 'Belgium' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'DE', name: 'Germany' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'PT', name: 'Portugal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'IE', name: 'Ireland' },
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' },
    { code: 'MA', name: 'Morocco' },
    { code: 'SN', name: 'Senegal' },
    { code: 'CI', name: "Côte d'Ivoire" },
    { code: 'CD', name: 'Congo (Kinshasa)' }
];

export const DEFAULT_REGION = 'CA';

/** The one to format with, whatever was stored. */
export function canonicalRegion(code) {
    const region = String(code || '')
        .trim()
        .toUpperCase();

    return regions.some(r => r.code === region) ? region : DEFAULT_REGION;
}

/** @type {{ code: string, name: string, flag: string }[]} */
export const regionOptions = regions.map(region => ({
    ...region,
    flag: region.code
        .split('')
        .map(char => String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 65))
        .join('')
}));

/**
 * What a sum looks like in this region, for the reader who is choosing one.
 *
 * Built with the reader's own language, because that is how they will see it
 * once it is saved — the region decides the separators, never the words.
 */
export function regionSample(code, language, currency) {
    try {
        return new Intl.NumberFormat(`${language || 'en'}-${canonicalRegion(code)}`, {
            style: 'currency',
            currency: currency || 'CAD',
            minimumFractionDigits: 2
        }).format(1234.5);
    } catch {
        return '';
    }
}
